import React, { useState, useEffect, useRef } from 'react';
import './Pricing.css';
import { FaInfoCircle, FaUsers, FaTasks, FaServer, FaTimes } from 'react-icons/fa';
import FeaturesModal from '../Features/FeaturesModal';

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(null);
  const dropdownRefs = useRef({});

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        isDropdownOpen &&
        dropdownRefs.current[isDropdownOpen] &&
        !dropdownRefs.current[isDropdownOpen].contains(event.target)
      ) {
        setIsDropdownOpen(null);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£'
  };

  const exchangeRates = {
    USD: 1,
    EUR: 0.85,
    GBP: 0.73
  };

  const commonFeatures = [
    'All features included',
    'Custom Branding',
  ];

  const commonFeatures2 = [
    'Knowledge Base',
    '24/7 Customer Support'
  ];

  const plans = [
    {
      name: 'Solo',
      monthlyPrice: 19,
      annualPrice: 190,
      storage: '1GB',
      employees: '0-5',
      features: [...commonFeatures,  ...commonFeatures2]
    },
    {
      name: 'Squad',
      monthlyPrice: 39,
      annualPrice: 390,
      storage: '2GB',
      employees: '6-15',
      features: [...commonFeatures,  ...commonFeatures2]
    },
    {
      name: 'Team',
      monthlyPrice: 79,
      annualPrice: 790,
      storage: '5GB',
      employees: '16-25',
      features: [...commonFeatures,  ...commonFeatures2,'Priority Chat Support']
    },
    {
      name: 'Scale',
      monthlyPrice: 149,
      annualPrice: 1490,
      storage: '10GB',
      employees: '25+',
      features: [...commonFeatures,  ...commonFeatures2, 'Priority Chat Support', 'Dedicated Account Manager']
    }
  ];

  const calculateSavings = (monthlyPrice, annualPrice) => {
    const yearlyTotal = monthlyPrice * 12;
    const savings = ((yearlyTotal - annualPrice) / yearlyTotal * 100).toFixed(0);
    return savings;
  };

  const getPrice = (plan) => {
    const basePrice = isAnnual ? plan.annualPrice : plan.monthlyPrice;
    return (basePrice * exchangeRates[selectedCurrency]).toFixed(2);
  };

  const getPeriod = () => {
    return isAnnual ? '/year' : '/mo';
  };

  // Full features list for the modal
  const allFeatures = [
    { name: 'Clients', description: 'Manage all your client details, project assignments, and interactions in one place.' },
    { name: 'Employees', description: 'Add, organize, and manage your team members, roles, and access levels.' },
    { name: 'Projects', description: 'Create and manage projects with deadlines, teams, milestones, and progress tracking.' },
    { name: 'Attendance', description: 'Track employee check-ins, check-outs, and working hours for accountability.' },
    { name: 'Tasks', description: 'Assign, prioritize, and monitor individual tasks across all projects.' },
    { name: 'Estimates', description: 'Send project quotes to clients and manage approvals before starting work.' },
    { name: 'Invoices', description: 'Generate, send, and track invoices with due dates and payment status.' },
    { name: 'Payments', description: 'Record received payments and track pending or overdue transactions.' },
    { name: 'Time Logs', description: 'Log time spent on tasks and projects to measure productivity and support billing.' },
    { name: 'Tickets', description: 'Manage client or internal support requests with a built-in ticketing system.' },
    { name: 'Events', description: 'Schedule and manage meetings, milestones, and important calendar events.' },
    { name: 'Notices', description: 'Share company-wide announcements and updates with your team.' },
    { name: 'Leaves', description: 'Track employee leave requests, approvals, and balances in one place.' },
    { name: 'Leads', description: 'Manage and nurture potential clients through your sales pipeline.' },
    { name: 'Holidays', description: 'Create and share your company’s holiday calendar with all employees.' },
    { name: 'Products', description: 'Add and manage physical or digital products offered by your business.' },
    { name: 'Expenses', description: 'Track business expenses by project, department, or category.' },
    { name: 'Contracts', description: 'Create, store, and manage legal agreements and contracts with clients or vendors.' },
    { name: 'Reports', description: 'Get detailed reports on finances, performance, time, and more.' },
    { name: 'Orders', description: 'Track client orders, statuses, and fulfillment for product or service sales.' },
    { name: 'Knowledge Base', description: 'Create articles, FAQs, and documentation to support your clients and team.' },
    { name: 'Bank Account', description: 'Store and manage company bank account details for financial tracking.' },
    { name: 'Messages', description: 'Send and receive direct messages with your team for quick communication.' },
    { name: 'Assets', description: 'Track company assets, equipment, and inventory across departments.' },
    { name: 'Biolinks', description: 'Create and manage custom link pages to share on social media or in bios.' },
    { name: 'Letter', description: 'Generate official HR letters like offer letters, warnings, or notices.' },
    { name: 'Payroll', description: 'Automate salary calculations, payslips, and employee compensation.' },
    { name: 'Performance', description: 'Evaluate and track employee performance through reviews and KPIs.' },
    { name: 'Purchase', description: 'Manage purchase requests, approvals, and vendor orders.' },
    { name: 'QR Code', description: 'Generate QR codes for links, employee check-ins, or marketing use.' },
    { name: 'Recruit', description: 'Post job openings, track applicants, and manage the hiring process.' },
    { name: 'Zoom', description: 'Schedule and join Zoom meetings directly from the platform.' },
    { name: 'Webhooks', description: 'Connect and automate third-party app integrations using webhooks.' },
    { name: 'SMS', description: 'Send SMS alerts and updates to clients or employees from the system.' },
    { name: 'Biometric', description: 'Integrate biometric devices for attendance and identity verification.' }
  ];

  // The 6 features to show in the dropdown (updated per user request)
  const dropdownFeatures = [
    'manage (Clients Employees Projects Tasks)',
    'Track Attendance',
    'Create Estimates',
    'Record Payments',
  ];


  return (
    <div id="pricing" className="pricing-section">
      <div className="pricing-header">
        <div className="pricing-label">Our Pricing</div>
        <h2 className="pricing-headline">Flexible Plans for Every Business</h2>
        <div className="pricing-subtitle">
          <svg className="pricing-subtitle-icon" width="22" height="22" viewBox="0 0 22 22" fill="none" style={{verticalAlign: 'middle', marginRight: '0.5em'}} xmlns="http://www.w3.org/2000/svg">
            <circle cx="11" cy="11" r="11" fill="#eaf3ff"/>
            <path d="M7 11.5L10 14.5L15 9.5" stroke="#2d6cdf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Simple, transparent pricing. No hidden fees.
        </div>
        
        <div className="currency-selector">
          {Object.keys(currencySymbols).map((currency) => (
            <button
              key={currency}
              className={`currency-btn ${selectedCurrency === currency ? 'active' : ''}`}
              onClick={() => setSelectedCurrency(currency)}
            >
              {currencySymbols[currency]} {currency}
            </button>
          ))}
        </div>

        <div className="pricing-toggle">
          <div className="switch-container">
            <span className={!isAnnual ? 'active' : ''}>Monthly</span>
            <label className="modern-switch">
              <input
                type="checkbox"
                checked={isAnnual}
                onChange={e => setIsAnnual(e.target.checked)}
                aria-checked={isAnnual}
                aria-label="Toggle annual pricing"
              />
              <span className="modern-slider" />
            </label>
            <span className={isAnnual ? 'active' : ''}>Annually</span>
          </div>
          {isAnnual && <div className="save-badge1">Save up to 17%</div>}
        </div>
      </div>
      
      <div className="pricing-container">
        {plans.map((plan) => (
          <div key={plan.name} className={`pricing-card ${plan.name.toLowerCase()}${plan.name === 'Team' ? ' popular' : ''}`}>
            <div className="pricing-card-header">
              <h3>{plan.name}
                {plan.name === 'Team' && (
                  <span className="popular-badge" aria-label="Most Popular">Most Popular</span>
                )}
              </h3>
              <div className="price">
                <span className="currency">{currencySymbols[selectedCurrency]}</span>
                <span className="amount">{getPrice(plan)}</span>
                <span className="period">{getPeriod()}</span>
              </div>
              {isAnnual && (
                <div className="savings">
                  Save {calculateSavings(plan.monthlyPrice, plan.annualPrice)}%
                </div>
              )}
            </div>
            <div className="pricing-card-body">
              <div className="key-feature-pill">
                <FaUsers className="key-feature-icon" />
                <span className="highlight-number">{plan.employees}</span>
                <span className="highlight-text"> Active Employees</span>
              </div>
              <div className="key-feature-pill">
                <FaTasks className="key-feature-icon" />
                <span className="highlight-number">Unlimited</span>
                <span className="highlight-text"> Projects & Tasks</span>
              </div>
              <div className={`key-feature-pill ${plan.name === 'Solo' || plan.name === 'Squad' ? 'key-feature-pill-disabled' : ''}`}>
                {plan.name === 'Solo' || plan.name === 'Squad' ? (
                  <>
                    <FaServer className="key-feature-icon" />
                    <span className="highlight-number">Server</span>
                    <span className="highlight-text"> Management</span>
                    <FaTimes className="key-feature-icon" style={{ marginLeft: 'auto' }} />
                  </>
                ) : (
                  <>
                    <FaServer className="key-feature-icon" />
                    <span className="highlight-number">Server</span>
                    <span className="highlight-text"> Management</span>
                  </>
                )}
              </div>
              <div className="features-list">
                {plan.features.map((feature, idx) => (
                  <React.Fragment key={feature}>
                    <div
                      className={`feature-item${feature === 'All features included' ? ' all-features-included' : ''}`}
                      style={feature === 'All features included' ? { position: 'relative' } : {}}
                    >
                      <div className="feature-text">
                        <span className="custom-green-check" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.4em' }}>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 10.5L9 14.5L15 7.5" stroke="#1bbf6b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        <span
                          className={feature === 'All features included' ? 'all-features-included-text' : ''}
                          style={feature === 'All features included' ? { color: '#2d6cdf', fontWeight: 600 } : {}}
                        >
                          {feature === 'All features included' ? 'All features included:' : feature}
                        </span>
                      </div>

                      {(feature === 'Custom Branding' || feature === 'Knowledge Base' || feature === '24/7 Customer Support' || feature === 'Priority Chat Support' || feature === 'Dedicated Account Manager') && (
                        <div className="feature-tooltip-right">
                          <div className="tooltip-trigger">
                            <div className="info-icon-wrapper">
                              <FaInfoCircle className="info-icon" />
                              <div className="tooltip">
                                {feature === 'Custom Branding' && 'Add your logo, colors, and brand identity to create a consistent experience for your clients.'}
                                {feature === 'Knowledge Base' && 'Centralized hub where customers can find answers through help articles, guides, and FAQs'}
                                {feature === '24/7 Customer Support' && 'Get help anytime with round-the-clock customer support available via chat, email, or phone.'}
                                {feature === 'Priority Chat Support' && 'Skip the queue and get faster responses from our support team through priority live chat.'}
                                {feature === 'Dedicated Account Manager' && 'Get personalized support and strategic guidance from your dedicated account manager.'}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* If this is 'All features included', show the dropdownFeatures as a subtitle list below it */}
                    {feature === 'All features included' && (
                      <div style={{ margin: '0.5em 0 1em 0', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5em',
                          width: '100%',
                          alignItems: 'center',
                        }}>
                          {dropdownFeatures.map((f, i) => (
                            f === 'manage (Clients Employees Projects Tasks)'
                              ? (
                                <div key={i} style={{ width: '90%', display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '0.2em' }}>
                                  <span
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      background: 'linear-gradient(90deg, #eaf3ff 60%, #f6faff 100%)',
                                      color: '#2d6cdf',
                                      borderRadius: '1.2em',
                                      fontWeight: 500,
                                      fontSize: '0.97em',
                                      boxShadow: '0 1px 6px rgba(45,108,223,0.07)',
                                      padding: '0.32em 1em 0.7em 1em',
                                      border: '1.5px solid #e0e7ef',
                                      width: '100%',
                                      letterSpacing: '0.01em',
                                      justifyContent: 'center',
                                      marginBottom: '0.3em',
                                      position: 'relative',
                                    }}
                                  >
                                    <span style={{ display: 'flex', alignItems: 'center', marginBottom: '0.3em' }}>
                                      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ marginRight: '0.3em' }} xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5 10.5L9 14.5L15 7.5" stroke="#1bbf6b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                      Manage
                                    </span>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5em', width: '100%', justifyContent: 'center' }}>
                                      {['Clients', 'Employees', 'Projects', 'Tasks'].map((item, idx) => (
                                        <span
                                          key={item}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: '#f6faff',
                                            color: '#2d6cdf',
                                            borderRadius: '1.2em',
                                            fontWeight: 500,
                                            fontSize: '0.93em',
                                            boxShadow: '0 1px 3px rgba(45,108,223,0.06)',
                                            padding: '0.22em 0.8em 0.22em 0.5em',
                                            border: '1.2px solid #e0e7ef',
                                            whiteSpace: 'nowrap',
                                            letterSpacing: '0.01em',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <svg width="15" height="15" viewBox="0 0 20 20" fill="none" style={{ marginRight: '0.2em' }} xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5 10.5L9 14.5L15 7.5" stroke="#1bbf6b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                          {item}
                                        </span>
                                      ))}
                                    </div>
                                  </span>
                                </div>
                              )
                              : (
                                <span
                                  key={i}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    background: 'linear-gradient(90deg, #eaf3ff 60%, #f6faff 100%)',
                                    color: '#2d6cdf',
                                    borderRadius: '1.2em',
                                    fontWeight: 500,
                                    fontSize: '0.97em',
                                    boxShadow: '0 1px 6px rgba(45,108,223,0.07)',
                                    padding: '0.32em 1em 0.32em 0.7em',
                                    border: '1.5px solid #e0e7ef',
                                    width: '90%',
                                    whiteSpace: 'nowrap',
                                    letterSpacing: '0.01em',
                                    justifyContent: 'center',
                                    marginBottom: '0.2em',
                                  }}
                                >
                                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ marginRight: '0.3em' }} xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 10.5L9 14.5L15 7.5" stroke="#1bbf6b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  {f}
                                </span>
                              )
                          ))}
                        </div>
                        <a
                          href="/features"
                          className="view-all-features-btn"
                          style={{
                            marginTop: '0.7em',
                            background: '#fff',
                            color: '#2d6cdf',
                            border: '1.2px solid #2d6cdf',
                            borderRadius: '1.2em',
                            padding: '0.32em 1em',
                            fontWeight: 500,
                            fontSize: '0.93em',
                            cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(45,108,223,0.06)',
                            transition: 'background 0.2s, color 0.2s, border 0.2s',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            position: 'relative',
                          }}
                        >
                          <span className="view-all-text">View All</span>
                        </a>
                      </div>
                    )}
                    {idx < plan.features.length - 1 && (
                      <div className="feature-divider"></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="pricing-card-footer">
              <button className="select-plan-btn" onClick={() => window.location.href = 'https://app.projectofi.com/signup'}>Choose Plan</button>
            </div>
          </div>
        ))}
      </div>

      <FeaturesModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        allFeatures={allFeatures}
      />
    </div>
  );
};

export default Pricing;

